/**
 * 视口裁剪 Hook - 优化大数据量渲染性能
 *
 * 核心原理：
 * 1. 监听画布变换事件（平移、缩放）
 * 2. 计算当前可视区域边界
 * 3. 使用 CSS display:none 隐藏视口外的节点 DOM
 * 4. 使用防抖避免频繁更新
 *
 * 优化策略：
 * - 不修改 LogicFlow 的 visible 属性（会影响交互）
 * - 直接操作 DOM 设置 display:none（不影响交互，但减少渲染）
 */

import { useCallback, useRef, useEffect } from 'react';
import type LogicFlow from '@logicflow/core';

interface ViewportBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface UseViewportCullingOptions {
  /** 是否启用视口裁剪，默认 true */
  enabled?: boolean;
  /** 视口边缘缓冲区（像素），默认 200 */
  buffer?: number;
  /** 防抖延迟（毫秒），默认 100 */
  debounceMs?: number;
  /** 节点数量阈值，超过此数量才启用裁剪，默认 100 */
  nodeThreshold?: number;
}

export const useViewportCulling = (
  lfRef: React.RefObject<LogicFlow | null>,
  options: UseViewportCullingOptions = {}
) => {
  const {
    enabled = true,
    buffer = 200,
    debounceMs = 100,
    nodeThreshold = 100,
  } = options;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastBoundsRef = useRef<ViewportBounds | null>(null);
  const isInitializedRef = useRef(false);

  /**
   * 计算当前视口边界（画布坐标系）
   */
  const getViewportBounds = useCallback((): ViewportBounds | null => {
    const lf = lfRef.current;
    if (!lf) return null;

    const { graphModel } = lf;
    const { transformModel, width, height } = graphModel;

    // 获取当前变换参数
    const transform = lf.getTransform();
    const scale = transform.SCALE_X || 1;
    const translateX = transform.TRANSLATE_X || 0;
    const translateY = transform.TRANSLATE_Y || 0;

    // 计算视口在画布坐标系中的边界
    const bufferInCanvas = buffer / scale;

    return {
      minX: -translateX / scale - bufferInCanvas,
      maxX: (-translateX + width) / scale + bufferInCanvas,
      minY: -translateY / scale - bufferInCanvas,
      maxY: (-translateY + height) / scale + bufferInCanvas,
    };
  }, [lfRef, buffer]);

  /**
   * 检查节点是否在视口内
   */
  const isNodeInViewport = useCallback(
    (
      node: { x: number; y: number; width?: number; height?: number },
      bounds: ViewportBounds
    ): boolean => {
      const nodeWidth = node.width || 100;
      const nodeHeight = node.height || 50;
      const halfWidth = nodeWidth / 2;
      const halfHeight = nodeHeight / 2;

      // 检查节点边界框是否与视口相交
      return !(
        node.x + halfWidth < bounds.minX ||
        node.x - halfWidth > bounds.maxX ||
        node.y + halfHeight < bounds.minY ||
        node.y - halfHeight > bounds.maxY
      );
    },
    []
  );

  // 节点可见性状态缓存
  const nodeVisibilityMapRef = useRef<Map<string, boolean>>(new Map());
  // DOM 元素缓存，避免重复查询
  const nodeDomCacheRef = useRef<Map<string, HTMLElement>>(new Map());
  // 上次缓存的节点数量
  const lastNodeCountRef = useRef<number>(0);

  /**
   * 缓存所有节点 DOM 元素
   */
  const cacheNodeDomElements = useCallback(() => {
    const lf = lfRef.current;
    if (!lf) return;

    // LogicFlow 的容器元素 - 使用更精确的选择器
    const lfContainer = (lf as any).container as HTMLElement;
    if (!lfContainer) {
      console.warn('📐 DOM 缓存: 未找到 LogicFlow 容器');
      return;
    }

    const nodes = lf.graphModel.nodes;
    // 只有节点数量变化时才重新缓存
    if (
      nodes.length === lastNodeCountRef.current &&
      nodeDomCacheRef.current.size > 0
    ) {
      return;
    }

    const startTime = performance.now();
    nodeDomCacheRef.current.clear();

    // LogicFlow 2.x 的 DOM 结构：
    // .lf-graph -> svg -> g.lf-base -> g.lf-node-group -> g[data-element-id]
    // 节点元素使用 data-element-id 属性标识

    // 首先尝试查找节点组容器
    const nodeGroup = lfContainer.querySelector('.lf-node-group');
    const searchContainer = nodeGroup || lfContainer;

    // 使用 data-element-id 选择器 - 这是 LogicFlow 2.x 的标准方式
    let allNodeEls = searchContainer.querySelectorAll(
      'g[data-element-id]'
    ) as NodeListOf<SVGGElement>;

    // 如果还是找不到，尝试从整个容器搜索
    if (allNodeEls.length === 0) {
      allNodeEls = lfContainer.querySelectorAll(
        'svg g[data-element-id]'
      ) as NodeListOf<SVGGElement>;
    }

    // 调试：输出找到的元素数量
    if (allNodeEls.length === 0) {
      // 输出容器结构以便调试
      const svgEl = lfContainer.querySelector('svg');
      const gEls = svgEl?.querySelectorAll('g');
      console.warn(
        `📐 DOM 缓存调试: SVG存在=${!!svgEl}, g元素数=${gEls?.length || 0}`
      );
      // 输出前几个 g 元素的属性
      if (gEls && gEls.length > 0) {
        const sampleAttrs = Array.from(gEls)
          .slice(0, 5)
          .map(g => ({
            id: g.id,
            class: g.className?.baseVal,
            dataId: g.getAttribute('data-element-id'),
          }));
        console.warn('📐 示例 g 元素:', sampleAttrs);
      }
    }

    allNodeEls.forEach((el: SVGGElement) => {
      const nodeId = el.getAttribute('data-element-id');
      // 只缓存节点，过滤掉边
      if (nodeId && !nodeId.includes('edge')) {
        nodeDomCacheRef.current.set(nodeId, el as unknown as HTMLElement);
      }
    });

    lastNodeCountRef.current = nodes.length;
    const elapsed = performance.now() - startTime;
    console.log(
      `📐 DOM 缓存完成: ${nodeDomCacheRef.current.size} 个元素 (节点数=${nodes.length}), 耗时=${elapsed.toFixed(1)}ms`
    );
  }, [lfRef]);

  /**
   * 更新节点可见性 - 使用 CSS display:none 方式
   */
  const updateNodeVisibility = useCallback(() => {
    const lf = lfRef.current;
    if (!lf || !enabled) return;

    const { graphModel } = lf;
    const nodes = graphModel.nodes;

    // 节点数量少于阈值时不启用裁剪
    if (nodes.length < nodeThreshold) {
      return;
    }

    const bounds = getViewportBounds();
    if (!bounds) return;

    // 检查边界是否有显著变化（避免不必要的更新）
    if (lastBoundsRef.current) {
      const last = lastBoundsRef.current;
      const threshold = 100; // 100像素的变化阈值
      if (
        Math.abs(bounds.minX - last.minX) < threshold &&
        Math.abs(bounds.maxX - last.maxX) < threshold &&
        Math.abs(bounds.minY - last.minY) < threshold &&
        Math.abs(bounds.maxY - last.maxY) < threshold
      ) {
        return; // 边界变化不大，跳过更新
      }
    }
    lastBoundsRef.current = bounds;

    // 确保 DOM 缓存已初始化
    if (nodeDomCacheRef.current.size === 0) {
      cacheNodeDomElements();
    }

    const startTime = performance.now();
    let visibleCount = 0;
    let hiddenCount = 0;
    let changedCount = 0;

    // 批量更新节点可见性
    nodes.forEach(nodeModel => {
      const isVisible = isNodeInViewport(
        {
          x: nodeModel.x,
          y: nodeModel.y,
          width: nodeModel.width,
          height: nodeModel.height,
        },
        bounds
      );

      const nodeId = nodeModel.id;
      const prevVisible = nodeVisibilityMapRef.current.get(nodeId);

      // 只有状态变化时才操作 DOM
      if (prevVisible !== isVisible) {
        nodeVisibilityMapRef.current.set(nodeId, isVisible);
        changedCount++;

        // 从缓存中获取 DOM 元素
        const nodeEl = nodeDomCacheRef.current.get(nodeId);
        if (nodeEl) {
          nodeEl.style.display = isVisible ? '' : 'none';
        }
      }

      if (isVisible) {
        visibleCount++;
      } else {
        hiddenCount++;
      }
    });

    const elapsed = performance.now() - startTime;
    // 只有有变化时才输出日志
    if (changedCount > 0) {
      console.log(
        `📐 视口裁剪: 可见=${visibleCount}, 隐藏=${hiddenCount}, 变化=${changedCount}, 耗时=${elapsed.toFixed(1)}ms`
      );
    }
  }, [
    lfRef,
    enabled,
    nodeThreshold,
    getViewportBounds,
    isNodeInViewport,
    cacheNodeDomElements,
  ]);

  /**
   * 防抖更新
   */
  const debouncedUpdate = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      updateNodeVisibility();
    }, debounceMs);
  }, [updateNodeVisibility, debounceMs]);

  /**
   * 显示所有节点（用于导出、保存等场景）
   */
  const showAllNodes = useCallback(() => {
    const lf = lfRef.current;
    if (!lf) return;

    const startTime = performance.now();
    let restoredCount = 0;

    // 使用缓存恢复所有节点的显示
    nodeDomCacheRef.current.forEach((nodeEl, nodeId) => {
      nodeVisibilityMapRef.current.set(nodeId, true);
      if (nodeEl.style.display === 'none') {
        nodeEl.style.display = '';
        restoredCount++;
      }
    });

    const elapsed = performance.now() - startTime;
    console.log(
      `📐 视口裁剪: 已显示 ${restoredCount} 个节点, 耗时=${elapsed.toFixed(1)}ms`
    );
  }, []);

  /**
   * 初始化视口裁剪
   */
  const initViewportCulling = useCallback(
    (lf: LogicFlow) => {
      if (!enabled || isInitializedRef.current) return;

      // 监听画布变换事件
      lf.on('graph:transform', debouncedUpdate);

      // 监听渲染完成事件，初次应用裁剪
      lf.on('graph:rendered', () => {
        // 延迟执行，确保节点已经渲染
        setTimeout(() => {
          // 先缓存 DOM 元素
          cacheNodeDomElements();
          // 然后应用裁剪
          updateNodeVisibility();
        }, 100);
      });

      isInitializedRef.current = true;
    },
    [enabled, debouncedUpdate, updateNodeVisibility, cacheNodeDomElements]
  );

  /**
   * 清理
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    initViewportCulling,
    updateNodeVisibility,
    showAllNodes,
    getViewportBounds,
    cacheNodeDomElements,
  };
};

export default useViewportCulling;
